import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { transcriptSyncIdentity, selectTranscriptAudioTuple } from '@/lib/transcripts/persistence';
import { isJoinableTranscriptIdentity, isMintedTranscriptIdentity, transcriptIdentityAliases, transcriptIdentitiesCanMatch } from '@/lib/transcripts/identityKind';

// LT-IDENTITY-DEDUP (catalog B1, 2026-09-10): the ASCII text normaliser and the
// word-set similarity that lived here are gone. They keyed six overlapping
// duplicate guards on TEXT, and the normaliser stripped every non-ASCII code
// point, so for zh/ja/ko/ru the guards were blind or wrong (two Mandarin
// sentences sharing one Latin token collapsed to that token and the second
// was dropped). Identity is the key now: the backend's segment id and
// sequence. Nothing else.

// The audio-window duplicate guard that stood here is gone too (review of
// catalog B1, 2026-09-10): Deepgram's word offsets restart at zero on every
// STT stream, and restarts are routine early in a call, so two genuine
// utterances minutes apart can share a window; without text the window was
// the only key and it dropped real speech. Its premise, two backend workers
// transcribing one track, is what the backend's restart sentinel prevents and
// rule 52 forbids — a routinely-active fallback is the defect, not a guard.

export type TranscriptionMessage = {
  /** Immutable persistence identity; never replace it during interim/final updates. */
  syncId?: string;
  name: string;
  message: string;
  timestamp: number;
  isSelf: boolean;
  isOfficer?: boolean; // Whether speaker is officer/staff (from transcriber is_officer flag)
  color: string;
  initial: string;
  isFinal?: boolean; // Track if this is a final or interim transcript
  participantIdentity?: string;
  segmentId?: string; // Unique ID to track segments across interim/final updates
  segmentSeq?: number; // Monotonically increasing sequence for ordering validation
  segmentKey?: string; // Backend/legacy key retained verbatim; never a display-order clock
  bubbleId?: string; // Groups consecutive segments from same speaker into UI bubbles
  audioStartTime?: number; // Deepgram seconds from audio stream start
  audioEndTime?: number; // Deepgram seconds from audio stream start
  /** Untrusted producer provenance; never implies a verified wall-clock origin. */
  audioTiming?: unknown;
  /**
   * Rule 57: a rescue is flagged as such. 'flush' = the backend's silence
   * flush published the provider's interim as this final; 'tail' = a
   * stream-ending close carried the provider's last interim out with it.
   * Undefined for a provider final. Persisted with the row so the after-call
   * transcript can tell a rescue from a provider final.
   */
  rescue?: 'flush' | 'tail';
};

// Transcription status for accurate status indicator
export type TranscriptionStatusValue =
  | 'connecting'
  | 'waiting'
  | 'transcribing'
  | 'paused'
  | 'error'
  | 'unavailable';

export interface TranscriptionStatus {
  status: TranscriptionStatusValue;
  details?: {
    message?: string;
    lastTranscriptTime?: number;
    audioSource?: string;
    errorType?: string;
    participantIdentity?: string;
    trackSid?: string;
    firstAudioAtMs?: number;
    firstNonSilentAtMs?: number;
    lastFrameMaxAbs?: number;
    traceId?: string;
  };
  timestamp: number;
}

// Active bubble tracking per speaker for bubble consolidation
export interface ActiveBubble {
  bubbleId: string;
  /** Local bubble admission time, used only for its existing timeout. */
  startTime: number;
}

export interface TranscriptionState {
  /** Accepted local slots: append a new identity; replace an existing slot in place. */
  transcripts: TranscriptionMessage[];
  /** Local UI identity allocator; survives clear/import, never source/engine evidence. */
  nextUiBubbleOrdinal?: number;
  // Participant-scoped segment identity -> highest accepted source sequence
  lastSeqBySegment: Record<string, number>;
  // Ring buffer of recent hashes to drop exact replays
  /** Dead since catalog B1 (2026-09-10): the interim hash ring is gone; the field stays until the persisted state shape is versioned (TL-15). */
  recentHashes: string[];
  // Current transcription status for status indicator
  transcriptionStatus: TranscriptionStatus;
  // Track active bubble per speaker for UI grouping (60s timeout or speaker switch)
  activeBubbles: Record<string, ActiveBubble>;
  // CRITICAL: Track finalized segment IDs - never show interims for these segments
  finalizedSegmentIds: Record<string, number>; // scoped segment identity -> local final-admission time
}

// Bubble timeout: start new bubble after 60 seconds of continuous speech
const BUBBLE_TIMEOUT_MS = 60000;

const initialState: TranscriptionState = {
  transcripts: [],
  nextUiBubbleOrdinal: 0,
  lastSeqBySegment: {},
  recentHashes: [],
  transcriptionStatus: {
    status: 'connecting',
    details: { message: 'Initializing...' },
    timestamp: Date.now(),
  },
  activeBubbles: {},
  finalizedSegmentIds: {},
};

/** Labels are used only for legacy UI rows; they never attest SourceTurnV1 ownership. */
function transcriptSpeaker(message: TranscriptionMessage): string {
  return message.participantIdentity || message.name || 'unknown';
}

/** Separate ID/key namespaces and participant boundaries without guessing a stream epoch. */
function segmentRegistryKeys(message: TranscriptionMessage): string[] {
  const speaker = transcriptSpeaker(message);
  const producer = isMintedTranscriptIdentity(message.syncId) ? [message.syncId] : [];
  const keys: string[] = [];
  if (message.segmentId) keys.push(JSON.stringify([speaker, ...producer, 'segment-id', message.segmentId]));
  if (message.segmentKey) keys.push(JSON.stringify([speaker, ...producer, 'segment-key', message.segmentKey]));
  return keys;
}

function transcriptMatchKeys(message: TranscriptionMessage): string[] {
  return transcriptIdentityAliases(message);
}

function sequenceRegistryKeys(message: TranscriptionMessage): string[] {
  const keys = segmentRegistryKeys(message);
  if (keys.length > 0 || !message.syncId) return keys;
  return [JSON.stringify([transcriptSpeaker(message), 'sync-id', message.syncId])];
}

function pruneFinalizedSegments(state: TranscriptionState, now: number): void {
  const retentionMs = 5 * 60 * 1000;
  for (const key of Object.keys(state.finalizedSegmentIds)) {
    if (now - state.finalizedSegmentIds[key] > retentionMs) delete state.finalizedSegmentIds[key];
  }
}

function hasFinalizedSegment(state: TranscriptionState, message: TranscriptionMessage): boolean {
  return segmentRegistryKeys(message).some(key => state.finalizedSegmentIds[key] !== undefined);
}

function recordAcceptedSegment(state: TranscriptionState, message: TranscriptionMessage, now: number): void {
  if (typeof message.segmentSeq === 'number' && Number.isFinite(message.segmentSeq)) {
    for (const key of sequenceRegistryKeys(message)) {
      state.lastSeqBySegment[key] = Math.max(state.lastSeqBySegment[key] ?? -Infinity, message.segmentSeq);
    }
  }
  if (message.isFinal) {
    for (const key of segmentRegistryKeys(message)) {
      // Replays and metadata corrections do not restart the final's local lifecycle.
      if (state.finalizedSegmentIds[key] === undefined) state.finalizedSegmentIds[key] = now;
    }
  }
}

/** Observe only identities minted by this local UI allocator, never source IDs. */
function reserveUiBubbleIdentity(state: TranscriptionState, bubbleId: string | undefined): void {
  const match = bubbleId?.match(/^bubble-.+-ui-(0|[1-9][0-9]*)$/);
  if (!match) return;
  const next = Number(match[1]) + 1;
  // The allocator uses JavaScript integer arithmetic; nonrepresentable imported
  // numbers are not allocator receipts. Their supplied ID itself stays intact.
  if (Number.isSafeInteger(next)) state.nextUiBubbleOrdinal = Math.max(state.nextUiBubbleOrdinal ?? 0, next);
}

function assignAppendedBubble(state: TranscriptionState, message: TranscriptionMessage, now: number): string {
  const speaker = transcriptSpeaker(message);
  const speakerKey = JSON.stringify([speaker]);
  const active = state.activeBubbles[speakerKey];
  const previous = state.transcripts[state.transcripts.length - 1];
  const switched = previous !== undefined && transcriptSpeaker(previous) !== speaker;
  const needsNew = !active || switched || now - active.startTime > BUBBLE_TIMEOUT_MS;
  let bubbleId = message.bubbleId ?? (needsNew ? undefined : active.bubbleId);
  if (!bubbleId) {
    const existingIds = new Set(state.transcripts.map(row => row.bubbleId).filter(Boolean));
    let ordinal = state.nextUiBubbleOrdinal ?? 0;
    do { bubbleId = `bubble-${encodeURIComponent(speaker)}-ui-${ordinal++}`; }
    while (existingIds.has(bubbleId));
    state.nextUiBubbleOrdinal = ordinal;
  }
  reserveUiBubbleIdentity(state, bubbleId);
  if (needsNew || message.bubbleId) state.activeBubbles[speakerKey] = { bubbleId, startTime: now };
  return bubbleId;
}

function appendAcceptedTranscript(state: TranscriptionState, incoming: TranscriptionMessage, now: number): void {
  const message = {
    ...incoming,
    syncId: transcriptSyncIdentity(incoming, state.transcripts.length),
    bubbleId: assignAppendedBubble(state, incoming, now),
  };
  state.transcripts.push(message);
  recordAcceptedSegment(state, message, now);
}

function replaceAcceptedTranscript(
  state: TranscriptionState, index: number, incoming: TranscriptionMessage, now: number,
): void {
  const existing = state.transcripts[index];
  const bubbleId = existing.bubbleId ?? resolveTranscriptionBubbleIds(state.transcripts)[index].bubbleId;
  const message = {
    ...incoming,
    ...selectTranscriptAudioTuple(existing, incoming),
    syncId: existing.syncId ?? transcriptSyncIdentity(incoming, index),
    bubbleId: bubbleId ?? incoming.bubbleId,
  };
  state.transcripts[index] = message;
  recordAcceptedSegment(state, message, now);
}

/** Imports retain supplied row order; it is not proof of historical callback order. */
function installImportedTranscripts(state: TranscriptionState, incoming: readonly TranscriptionMessage[], now: number): void {
  const identified = incoming.map((message, index) => ({ ...message, syncId: transcriptSyncIdentity(message, index) }));
  state.transcripts = resolveTranscriptionBubbleIds(identified).map(message => ({ ...message }));
  state.lastSeqBySegment = {};
  state.finalizedSegmentIds = {};
  state.activeBubbles = {};
  for (const message of state.transcripts) {
    reserveUiBubbleIdentity(state, message.bubbleId);
    recordAcceptedSegment(state, message, now);
    const speaker = transcriptSpeaker(message);
    const speakerKey = JSON.stringify([speaker]);
    const active = state.activeBubbles[speakerKey];
    if (!active || active.bubbleId !== message.bubbleId) {
      state.activeBubbles[speakerKey] = { bubbleId: message.bubbleId!, startTime: now };
    }
  }
}

function buildTranscriptIndex(messages: readonly TranscriptionMessage[]): Map<string, number[]> {
  const index = new Map<string, number[]>();
  messages.forEach((message, slot) => indexAcceptedTranscript(index, message, slot));
  return index;
}

function indexAcceptedTranscript(index: Map<string, number[]>, message: TranscriptionMessage, slot: number): void {
  for (const key of transcriptMatchKeys(message)) {
    const slots = index.get(key) ?? [];
    if (!slots.includes(slot)) slots.push(slot);
    index.set(key, slots);
  }
}

function indexedTranscriptSlot(
  index: Map<string, number[]>, message: TranscriptionMessage, messages: readonly TranscriptionMessage[],
): number {
  for (const key of transcriptMatchKeys(message)) {
    const slots = index.get(key) ?? [];
    for (let position = slots.length - 1; position >= 0; position -= 1) {
      const slot = slots[position];
      if (transcriptIdentitiesCanMatch(messages[slot], message)) return slot;
    }
  }
  return -1;
}

function existingTranscriptIndex(state: TranscriptionState, incoming: TranscriptionMessage): number {
  return state.transcripts.findIndex(row => transcriptIdentitiesCanMatch(row, incoming));
}

const transcriptionSlice = createSlice({
  name: 'transcription',
  initialState,
  reducers: {
    setTranscripts: (state, action: PayloadAction<readonly TranscriptionMessage[]>) => {
      installImportedTranscripts(state, action.payload, Date.now());
    },

    /** Reconnect batches append new identities in supplied order and update existing slots. */
    mergeTranscripts: (state, action: PayloadAction<readonly TranscriptionMessage[]>) => {
      const now = Date.now();
      pruneFinalizedSegments(state, now);
      const index = buildTranscriptIndex(state.transcripts);
      for (const incoming of action.payload) {
        if (!incoming.isFinal && hasFinalizedSegment(state, incoming)) continue;
        const existingIndex = indexedTranscriptSlot(index, incoming, state.transcripts);
        if (existingIndex < 0) {
          appendAcceptedTranscript(state, incoming, now);
          indexAcceptedTranscript(index, state.transcripts[state.transcripts.length - 1], state.transcripts.length - 1);
          continue;
        }
        const existing = state.transcripts[existingIndex];
        const hasNewerSourceSequence = typeof incoming.segmentSeq === 'number' && Number.isFinite(incoming.segmentSeq) &&
          typeof existing.segmentSeq === 'number' && Number.isFinite(existing.segmentSeq) &&
          incoming.segmentSeq > existing.segmentSeq;
        const shouldReplace = (incoming.isFinal && !existing.isFinal) ||
          (incoming.isFinal === existing.isFinal && hasNewerSourceSequence);
        if (shouldReplace) {
          replaceAcceptedTranscript(state, existingIndex, incoming, now);
          indexAcceptedTranscript(index, state.transcripts[existingIndex], existingIndex);
        }
      }
    },

    /** Authoritative same-key persistence winners retain the accepted slot and row identities. */
    reconcilePersistedTranscripts: (state, action: PayloadAction<readonly TranscriptionMessage[]>) => {
      const now = Date.now();
      pruneFinalizedSegments(state, now);
      const index = buildTranscriptIndex(state.transcripts);
      for (const incoming of action.payload) {
        if (!incoming.isFinal && hasFinalizedSegment(state, incoming)) continue;
        const existingIndex = indexedTranscriptSlot(index, incoming, state.transcripts);
        if (existingIndex < 0) {
          appendAcceptedTranscript(state, incoming, now);
          indexAcceptedTranscript(index, state.transcripts[state.transcripts.length - 1], state.transcripts.length - 1);
        }
        else if (!(state.transcripts[existingIndex].isFinal && !incoming.isFinal)) {
          replaceAcceptedTranscript(state, existingIndex, incoming, now);
          indexAcceptedTranscript(index, state.transcripts[existingIndex], existingIndex);
        }
      }
    },

    clearTranscripts: (state) => {
      // A legacy preloaded state may bypass import actions and lack a serial.
      for (const message of state.transcripts) reserveUiBubbleIdentity(state, message.bubbleId);
      state.transcripts = [];
      state.lastSeqBySegment = {};
      state.recentHashes = [];
      state.activeBubbles = {};
      state.finalizedSegmentIds = {};
      state.transcriptionStatus = {
        status: 'connecting', details: { message: 'Initializing...' }, timestamp: Date.now(),
      };
    },

    setTranscriptionStatus: (state, action: PayloadAction<TranscriptionStatus>) => {
      state.transcriptionStatus = action.payload;
    },

    hydrateTranscriptionState: (_state, action: PayloadAction<TranscriptionState>) => {
      const state = { ...action.payload,
        nextUiBubbleOrdinal: Math.max(_state.nextUiBubbleOrdinal ?? 0, action.payload.nextUiBubbleOrdinal ?? 0),
      };
      const now = Date.now();
      installImportedTranscripts(state, action.payload.transcripts, now);
      // Only already-scoped lifecycle receipts can survive a local-state restore.
      // Old global segment keys cannot be attributed to an owner or an epoch.
      for (const key of Object.keys(state.finalizedSegmentIds)) {
        const finalizedAt = action.payload.finalizedSegmentIds[key];
        if (typeof finalizedAt === 'number' && Number.isFinite(finalizedAt)) state.finalizedSegmentIds[key] = finalizedAt;
      }
      for (const key of Object.keys(state.lastSeqBySegment)) {
        const sequence = action.payload.lastSeqBySegment[key];
        if (typeof sequence === 'number' && Number.isFinite(sequence)) {
          state.lastSeqBySegment[key] = Math.max(state.lastSeqBySegment[key], sequence);
        }
      }
      pruneFinalizedSegments(state, now);
      return state;
    },

    /** Legacy append accepts immutable transport identity, never repeated-text similarity. */
    addTranscript: (state, action: PayloadAction<TranscriptionMessage>) => {
      const incoming = action.payload;
      const now = Date.now();
      pruneFinalizedSegments(state, now);
      if (!incoming.isFinal && hasFinalizedSegment(state, incoming)) return;
      // This legacy API appends speech; a process-local segment ID does not
      // prove that two explicit finals are one utterance. Only an actual
      // producer sync ID can suppress an append replay. Corrections continue
      // through updateOrAddTranscript/reconcile with their existing slots.
      const replay = isJoinableTranscriptIdentity(incoming.syncId) &&
        state.transcripts.some(message => message.syncId === incoming.syncId && transcriptIdentitiesCanMatch(message, incoming));
      if (!replay) appendAcceptedTranscript(state, incoming, now);
    },

    updateOrAddTranscript: (
      state,
      action: PayloadAction<{
        transcript: TranscriptionMessage;
        isFinal: boolean;
        participantIdentity: string;
        segmentId?: string;
        segmentSeq?: number;
        rawTimestamp?: number;
      }>,
    ) => {
      const { transcript: raw, isFinal, participantIdentity, segmentId, segmentSeq } = action.payload;
      const now = Date.now();
      const timestamp = !raw.timestamp ? now : raw.timestamp < 1e12 ? raw.timestamp * 1000 : raw.timestamp;
      const incoming: TranscriptionMessage = {
        ...raw,
        timestamp,
        isFinal,
        participantIdentity: participantIdentity || raw.participantIdentity || 'unknown',
        segmentId: segmentId ?? raw.segmentId,
        segmentKey: segmentId ?? raw.segmentId ?? raw.segmentKey,
        segmentSeq: segmentSeq ?? raw.segmentSeq,
      };
      // A keyless legacy delivery has no proven update identity. Allocate its own
      // accepted slot instead of collapsing unrelated speech in a clock bucket.
      pruneFinalizedSegments(state, now);
      if (!isFinal && hasFinalizedSegment(state, incoming)) return;
      const existingIndex = existingTranscriptIndex(state, incoming);
      if (existingIndex >= 0) {
        const existing = state.transcripts[existingIndex];
        if (existing.isFinal && !isFinal) return;
        const knownSequences = sequenceRegistryKeys(incoming).map(key => state.lastSeqBySegment[key] ?? -Infinity);
        if (typeof existing.segmentSeq === 'number' && Number.isFinite(existing.segmentSeq)) knownSequences.push(existing.segmentSeq);
        const lastSeq = Math.max(-Infinity, ...knownSequences);
        if (typeof incoming.segmentSeq === 'number' && Number.isFinite(incoming.segmentSeq) && incoming.segmentSeq < lastSeq) return;
        // A correction replaces this slot; it does not count as a new speaker
        // event or allocate/change the active bubble used by the next new row.
        replaceAcceptedTranscript(state, existingIndex, { ...existing, ...incoming, ...selectTranscriptAudioTuple(existing, incoming), isFinal: existing.isFinal || isFinal }, now);
      } else {
        appendAcceptedTranscript(state, incoming, now);
      }
    },
  },
});

export const {
  addTranscript, setTranscripts, mergeTranscripts, reconcilePersistedTranscripts,
  clearTranscripts, setTranscriptionStatus, hydrateTranscriptionState, updateOrAddTranscript,
} = transcriptionSlice.actions;
export default transcriptionSlice.reducer;

export interface TranscriptionBubble {
  bubbleId: string;
  speaker: string; // participantIdentity or name
  speakerName: string; // Display name
  startTime: number;
  endTime: number;
  messages: TranscriptionMessage[];
  combinedText: string; // Visible final/interim words in accepted slot order
  settledText: string; // Finalized text only; never becomes interim again
  interimText: string; // Current unfinalized tail only
  hasInterim: boolean; // true if any message is still interim (still receiving)
  isSelf: boolean; // Is this the local user?
  color: string; // Speaker color
  initial: string; // Speaker initial
}

const selectTranscripts = (state: { transcription: TranscriptionState }) => state.transcription.transcripts;

/** Missing legacy IDs follow supplied/accepted slots; clocks serve only the existing timeout. */
export function resolveTranscriptionBubbleIds(
  transcripts: readonly TranscriptionMessage[],
): readonly TranscriptionMessage[] {
  if (transcripts.every(message => message.bubbleId)) return transcripts;
  const usedIds = new Set(transcripts.map(message => message.bubbleId).filter(Boolean));
  let currentBubbleId = '';
  let currentSpeaker = '';
  let bubbleStartTime = 0;
  return transcripts.map((message, index) => {
    const speaker = transcriptSpeaker(message);
    if (message.bubbleId) {
      if (message.bubbleId !== currentBubbleId) bubbleStartTime = message.timestamp;
      currentSpeaker = speaker;
      currentBubbleId = message.bubbleId;
      return message;
    }
    if (!currentBubbleId || speaker !== currentSpeaker || message.timestamp - bubbleStartTime > BUBBLE_TIMEOUT_MS) {
      let slot = index;
      do { currentBubbleId = `computed-${encodeURIComponent(speaker)}-slot-${slot++}`; }
      while (usedIds.has(currentBubbleId));
      usedIds.add(currentBubbleId);
      currentSpeaker = speaker;
      bubbleStartTime = message.timestamp;
    }
    return { ...message, bubbleId: currentBubbleId };
  });
}

/** One visibility/order rule for the source selector and Both member projection. */
export function resolveVisibleTranscriptionMessages(
  messages: readonly TranscriptionMessage[],
): readonly TranscriptionMessage[] {
  const finalized = new Set(messages.filter(message => message.isFinal).flatMap(segmentRegistryKeys));
  const visible: TranscriptionMessage[] = [];
  const interimPositionByIdentity = new Map<string, number>();
  for (const [index, message] of messages.entries()) {
    if (!message.message?.trim()) continue;
    if (message.isFinal) { visible.push(message); continue; }
    if (segmentRegistryKeys(message).some(key => finalized.has(key))) continue;
    const key = segmentRegistryKeys(message)[0] ??
      (message.syncId ? JSON.stringify([transcriptSpeaker(message), 'sync-id', message.syncId]) : `unkeyed:${index}`);
    const position = interimPositionByIdentity.get(key);
    if (position === undefined) {
      interimPositionByIdentity.set(key, visible.length);
      visible.push(message);
    } else visible[position] = message;
  }
  return visible;
}

/** First accepted slot fixes each bubble's display order; source clocks remain seek metadata. */
export const selectTranscriptionBubbles = createSelector(
  [selectTranscripts],
  (transcripts: TranscriptionMessage[]): TranscriptionBubble[] => {
    const bubbleMap = new Map<string, TranscriptionBubble>();
    for (const message of resolveTranscriptionBubbleIds(transcripts)) {
      const bubbleId = message.bubbleId!;
      if (!bubbleMap.has(bubbleId)) {
        bubbleMap.set(bubbleId, {
          bubbleId, speaker: transcriptSpeaker(message), speakerName: message.name,
          startTime: message.timestamp, endTime: message.timestamp, messages: [],
          combinedText: '', settledText: '', interimText: '', hasInterim: false,
          isSelf: message.isSelf, color: message.color, initial: message.initial,
        });
      }
      const bubble = bubbleMap.get(bubbleId)!;
      bubble.messages.push(message);
      bubble.endTime = Math.max(bubble.endTime, message.timestamp);
    }
    for (const bubble of bubbleMap.values()) {
      const visible = resolveVisibleTranscriptionMessages(bubble.messages);
      const finals = visible.filter(message => message.isFinal);
      const interims = visible.filter(message => !message.isFinal);
      bubble.settledText = finals.map(message => message.message).join(' ').trim();
      bubble.interimText = interims.map(message => message.message).join(' ').trim();
      bubble.combinedText = visible.map(message => message.message).join(' ').trim();
      bubble.hasInterim = interims.length > 0;
      if (!bubble.combinedText && bubble.messages.length > 0) {
        bubble.combinedText = bubble.messages.find(message => message.message?.trim())?.message ?? '';
      }
    }
    return Array.from(bubbleMap.values());
  },
);
